import React, { useState } from "react";
import { LineTable } from "./NpdShared";
import { errorMessage, npdApi } from "./npdApi";

const EMPTY = { id: null, code: "", name: "", product: "", stages: [] };

// Assembly line master data: the admin adds lines here together with the line's development stages.
// Stages are fixed once the line is created (parts store their stage by position); they can only be renamed.
const AssemblyLines = ({ app }) => {
  const { isAdmin, lines, parts, go, onLineSaved, toast } = app;
  const [form, setForm] = useState(EMPTY);
  const [newStage, setNewStage] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const editing = form.id !== null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const edit = (l) => {
    setForm({ id: l.id, code: l.code, name: l.name, product: l.product, stages: [...(l.stages || [])] });
    setNewStage("");
    setErr("");
  };

  const cancel = () => {
    setForm(EMPTY);
    setNewStage("");
    setErr("");
  };

  const setStage = (i, value) =>
    setForm((f) => ({ ...f, stages: f.stages.map((s, j) => (j === i ? value : s)) }));

  // Adding, removing and reordering stages is only possible while creating the line
  const addStage = () => {
    const name = newStage.trim();
    if (!name) return setErr("Enter a stage name.");
    if (form.stages.some((s) => s.trim().toLowerCase() === name.toLowerCase())) {
      return setErr(`Stage "${name}" is already in the list.`);
    }
    setForm((f) => ({ ...f, stages: [...f.stages, name] }));
    setNewStage("");
    setErr("");
  };

  const removeStage = (i) => setForm((f) => ({ ...f, stages: f.stages.filter((_, j) => j !== i) }));

  const moveStage = (i, step) =>
    setForm((f) => {
      const stages = [...f.stages];
      const j = i + step;
      if (j < 0 || j >= stages.length) return f;
      [stages[i], stages[j]] = [stages[j], stages[i]];
      return { ...f, stages };
    });

  const save = async () => {
    if (!editing && !form.code.trim()) return setErr("Enter a line code.");
    if (!form.name.trim()) return setErr("Enter a line name.");
    if (!form.product.trim()) return setErr("Enter the product.");
    const stages = form.stages.map((s) => s.trim());
    if (!stages.length) return setErr("Add at least one development stage.");
    const empty = stages.findIndex((s) => !s);
    if (empty >= 0) return setErr(`Stage ${empty + 1} has no name.`);
    const dup = stages.find((s, i) => stages.findIndex((t) => t.toLowerCase() === s.toLowerCase()) !== i);
    if (dup) return setErr(`Stage "${dup}" is used more than once.`);
    setErr("");
    setSaving(true);
    try {
      const body = { code: form.code.trim(), name: form.name.trim(), product: form.product.trim(), stages };
      const l = editing ? await npdApi.updateLine(form.id, body) : await npdApi.addLine(body);
      onLineSaved(l);
      toast(`${l.name} ${editing ? "updated" : "added"}`);
      cancel();
    } catch (e) {
      setErr(errorMessage(e, "Could not save the assembly line."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h2>Assembly Lines</h2>
      <p className="sub">Click a line to open it in the Development Tracker</p>
      <div className="panel">
        <div className="wrap">
          {lines.length ? (
            <LineTable lines={lines} parts={parts} onOpen={(l) => go("tracker", { line: l.id })} onEdit={isAdmin ? edit : undefined} />
          ) : <div className="empty">No assembly lines yet. Add the first one below.</div>}
        </div>
      </div>
      {isAdmin && (
        <div className="panel">
          <h3>{editing ? `Edit ${form.name || "assembly line"}` : "Add assembly line"}</h3>
          <div className="fm">
            <label>Line code (part number prefix)
              <input value={form.code} onChange={set("code")} placeholder="e.g. A" maxLength={10} disabled={editing} />
            </label>
            <label>Line name
              <input value={form.name} onChange={set("name")} placeholder="e.g. Assembly Line A" />
            </label>
            <label>Product
              <input value={form.product} onChange={set("product")} placeholder="e.g. Chassis & Frame" />
            </label>
          </div>

          <h3 style={{ marginTop: 16 }}>Development stages</h3>
          <p className="mut" style={{ margin: "0 0 8px" }}>
            {editing
              ? "Stages are fixed once the line is created; you can rename them here."
              : "Add the stages in order. After the line is created, stages can only be renamed, not added, removed or reordered."}
          </p>
          {form.stages.length ? (
            <div className="wrap">
              <table>
                <tbody>
                  {form.stages.map((s, i) => (
                    <tr key={i}>
                      <td style={{ width: 40 }}>{i + 1}.</td>
                      <td>
                        <input value={s} onChange={(e) => setStage(i, e.target.value)} style={{ width: "100%", margin: "0" }} />
                      </td>
                      {!editing && (
                        <td style={{ width: 150, whiteSpace: "nowrap" }}>
                          <button type="button" className="btn s" onClick={() => moveStage(i, -1)} disabled={i === 0}>↑</button>{" "}
                          <button type="button" className="btn s" onClick={() => moveStage(i, 1)} disabled={i === form.stages.length - 1}>↓</button>{" "}
                          <button type="button" className="btn s" onClick={() => removeStage(i)}>Remove</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty">No stages added yet.</div>
          )}
          {!editing && (
            <span className="inl" style={{ marginTop: 8 }}>
              <input style={{margin: "0"}} value={newStage} placeholder="New stage name, e.g. Study of Drawing"
                onChange={(e) => setNewStage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addStage()} />
              <button type="button" className="btn" onClick={addStage}>Add stage</button>
            </span>
          )}

          <div className="red" style={{ marginTop: 8 }}>{err}</div>
          <div className="tools" style={{ marginTop: 10 }}>
            <button className="btn p" onClick={save} disabled={saving}>{editing ? "Save changes" : "Add assembly line"}</button>
            {editing && <button className="btn" onClick={cancel}>Cancel</button>}
          </div>
        </div>
      )}
    </>
  );
};

export default AssemblyLines;
